import React from 'react';
import { motion } from 'framer-motion';
import { X, Download, Share2, FileText, Moon, Heart, Battery } from 'lucide-react';
import { format } from 'date-fns';

interface HealthReportProps {
  metrics: {
    respiratoryRate: number;
    bloodOxygen: number;
    restingHR: number;
    hrv: number;
    skinTemp: number;
    sleepScore?: number;
    avgHeartRate?: number;
    recovery?: number;
  };
  onClose: () => void;
}

export function HealthReport({ metrics, onClose }: HealthReportProps) {
  const handleDownloadPDF = () => {
    // PDF generation logic would go here
    console.log('Downloading PDF report...');
  };

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-background rounded-xl border border-border w-full max-w-2xl overflow-hidden"
      >
        {/* Header */}
        <div className="p-4 border-b border-border flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold">Health Report</h2>
            <p className="text-sm text-muted-foreground">
              {format(new Date(), 'MMMM d, yyyy')}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Report Content */}
        <div className="p-6 space-y-6">
          {/* Performance Metrics */}
          <div className="grid grid-cols-3 gap-4">
            <div className="theme-card p-4">
              <div className="flex items-center gap-2 mb-2">
                <Battery className="w-4 h-4 text-primary" />
                <span className="text-sm font-medium">Recovery</span>
              </div>
              <div className="text-2xl font-bold">{metrics.recovery || 85}%</div>
              <div className="text-xs text-success">Optimal range</div>
            </div>
            <div className="theme-card p-4">
              <div className="flex items-center gap-2 mb-2">
                <Moon className="w-4 h-4 text-primary" />
                <span className="text-sm font-medium">Sleep</span>
              </div>
              <div className="text-2xl font-bold">{metrics.sleepScore || 92}%</div>
              <div className="text-xs text-success">Above baseline</div>
            </div>
            <div className="theme-card p-4">
              <div className="flex items-center gap-2 mb-2">
                <Heart className="w-4 h-4 text-primary" />
                <span className="text-sm font-medium">Avg HR</span>
              </div>
              <div className="text-2xl font-bold">{metrics.avgHeartRate || 68}</div>
              <div className="text-xs text-success">Within range</div>
            </div>
          </div>

          {/* Vital Signs */}
          <div className="theme-card p-4">
            <h3 className="text-sm font-medium mb-4">Vital Signs</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-sm text-muted-foreground">Respiratory Rate</div>
                <div className="text-lg font-semibold">{metrics.respiratoryRate} rpm</div>
                <div className="text-xs text-success">Within normal range</div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground">Blood Oxygen</div>
                <div className="text-lg font-semibold">{metrics.bloodOxygen}%</div>
                <div className="text-xs text-success">Optimal</div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground">Resting Heart Rate</div>
                <div className="text-lg font-semibold">{metrics.restingHR} bpm</div>
                <div className="text-xs text-success">Athletic range</div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground">HRV</div>
                <div className="text-lg font-semibold">{metrics.hrv} ms</div>
                <div className="text-xs text-success">Above baseline</div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground">Skin Temperature</div>
                <div className="text-lg font-semibold">{metrics.skinTemp}°F</div>
                <div className="text-xs text-warning">Above normal range</div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <button 
              onClick={handleDownloadPDF}
              className="flex-1 theme-button py-2 rounded-lg flex items-center justify-center gap-2"
            >
              <Download className="w-4 h-4" />
              Download PDF
            </button>
            <button className="p-2 rounded-lg bg-muted hover:bg-muted/70 transition-colors">
              <Share2 className="w-4 h-4" />
            </button>
            <button className="p-2 rounded-lg bg-muted hover:bg-muted/70 transition-colors">
              <FileText className="w-4 h-4" />
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}